import axios from 'axios';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '../stores/auth';

// API base URL - Railway backend in production
const BACKEND_URL = 'https://backend-production-ca63a.up.railway.app';
const API_BASE_URL = import.meta.env.DEV ? '/api' : `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Socket.io client - connect to backend server
const SOCKET_URL = import.meta.env.DEV ? 'http://localhost:3000' : BACKEND_URL;
export const socket: Socket = io(SOCKET_URL, {
  autoConnect: false,
  transports: ['websocket', 'polling'],
});

// Connect socket when user is authenticated
export const connectSocket = () => {
  const token = useAuthStore.getState().token;
  if (token && !socket.connected) {
    socket.auth = { token };
    socket.connect();
  }
};

export const disconnectSocket = () => {
  if (socket.connected) {
    socket.disconnect();
  }
};

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      useAuthStore.getState().logout();
      disconnectSocket();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  register: (email: string, password: string, name?: string) =>
    api.post('/auth/register', { email, password, name }),
  me: () => api.get('/auth/me'),
};

// Workflows
export const workflowApi = {
  list: (params?: { page?: number; limit?: number; search?: string }) =>
    api.get('/workflows', { params }),
  get: (id: string) => api.get(`/workflows/${id}`),
  create: (data: { name: string; description?: string }) =>
    api.post('/workflows', data),
  update: (id: string, data: Record<string, unknown>) =>
    api.put(`/workflows/${id}`, data),
  delete: (id: string) => api.delete(`/workflows/${id}`),
  duplicate: (id: string) => api.post(`/workflows/${id}/duplicate`),
  execute: (id: string, input?: Record<string, unknown>) =>
    api.post(`/executions/execute/${id}`, { input }),
};

// Executions
export const executionApi = {
  list: (workflowId: string, params?: { page?: number; status?: string }) =>
    api.get(`/executions/workflow/${workflowId}`, { params }),
  get: (id: string) => api.get(`/executions/${id}`),
  cancel: (id: string) => api.post(`/executions/${id}/cancel`),
  retry: (id: string) => api.post(`/executions/${id}/retry`),
};

// Templates
export const templateApi = {
  list: (params?: { category?: string; difficulty?: string; search?: string }) =>
    api.get('/templates', { params }),
  get: (id: string) => api.get(`/templates/${id}`),
  import: (id: string) => api.post(`/templates/${id}/import`),
  categories: () => api.get('/templates/categories'),
};

// Models
export const modelApi = {
  list: (params?: { page?: number; status?: string; task?: string }) =>
    api.get('/models', { params }),
  get: (id: string) => api.get(`/models/${id}`),
  create: (data: {
    name: string;
    description?: string;
    baseModel: string;
    task: string;
  }) => api.post('/models', data),
  update: (id: string, data: Record<string, unknown>) =>
    api.put(`/models/${id}`, data),
  delete: (id: string) => api.delete(`/models/${id}`),
  train: (id: string, datasetId: string, config?: Record<string, unknown>) =>
    api.post(`/models/${id}/train`, { datasetId, config }),
  jobs: (id: string) => api.get(`/models/${id}/jobs`),
  cancelJob: (modelId: string, jobId: string) =>
    api.post(`/models/${modelId}/jobs/${jobId}/cancel`),
  hub: (params?: { page?: number; task?: string; search?: string; sort?: string }) =>
    api.get('/models/hub/browse', { params }),
};

// Datasets
export const datasetApi = {
  list: (params?: { page?: number; format?: string }) =>
    api.get('/datasets', { params }),
  get: (id: string) => api.get(`/datasets/${id}`),
  create: (data: { name: string; description?: string; format: string }) =>
    api.post('/datasets', data),
  delete: (id: string) => api.delete(`/datasets/${id}`),
  preview: (id: string, rows?: number) =>
    api.get(`/datasets/${id}/preview`, { params: { rows } }),
  downloadUrl: (id: string) => api.get(`/datasets/${id}/download-url`),
};

// Inference
export const inferenceApi = {
  generate: (modelId: string, prompt: string, options?: Record<string, unknown>) =>
    api.post('/inference/generate', { modelId, prompt, ...options }),
  chat: (modelId: string, messages: Array<{ role: string; content: string }>) =>
    api.post('/inference/chat', { modelId, messages }),
  models: () => api.get('/inference/models'),
  health: () => api.get('/inference/health'),
};

// Credentials
export const credentialApi = {
  list: () => api.get('/credentials'),
  get: (id: string) => api.get(`/credentials/${id}`),
  create: (data: { name: string; type: string; data: Record<string, unknown> }) =>
    api.post('/credentials', data),
  update: (id: string, data: Record<string, unknown>) =>
    api.put(`/credentials/${id}`, data),
  delete: (id: string) => api.delete(`/credentials/${id}`),
  test: (id: string) => api.post(`/credentials/${id}/test`),
};

export { api };
export default api;
